import React, { useState, useEffect } from "react";
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  Alert, 
  Spinner,
  Modal
} from "react-bootstrap";
import { FaSave, FaUndo, FaTrash, FaToggleOn, FaToggleOff, FaPlus } from "react-icons/fa";

const FeaturesSectionAdmin = () => {
  const [featuresData, setFeaturesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);

  useEffect(() => {
    fetchFeaturesData();
  }, []);

  const fetchFeaturesData = async () => {
    try {
      const response = await fetch("https://ecom-22-fe6311cae608.herokuapp.com/api/features-section");
      const data = await response.json();
      
      if (data.success) {
        setFeaturesData(data.data);
      } else {
        setError(data.message || "Failed to fetch features section data");
      }
    } catch (err) {
      setError("Network error. Please try again later.");
      console.error("Error fetching features section:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFeaturesData({
      ...featuresData,
      [name]: value
    });
  };

  const handleFeatureChange = (index, value) => {
    const updatedFeatures = [...featuresData.features];
    updatedFeatures[index] = value;
    setFeaturesData({
      ...featuresData,
      features: updatedFeatures
    });
  };

  const addFeature = () => {
    setFeaturesData({
      ...featuresData,
      features: [...featuresData.features, ""]
    });
  };

  const removeFeature = (index) => {
    const updatedFeatures = [...featuresData.features];
    updatedFeatures.splice(index, 1);
    setFeaturesData({
      ...featuresData,
      features: updatedFeatures
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch("https://ecom-22-fe6311cae608.herokuapp.com/api/features-section", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(featuresData)
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess("Features section updated successfully!");
        setFeaturesData(data.data);
      } else {
        setError(data.message || "Failed to update features section");
      }
    } catch (err) {
      setError("Network error. Please try again later.");
      console.error("Error updating features section:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    try {
      const response = await fetch("https://ecom-22-fe6311cae608.herokuapp.com/api/features-section/toggle", {
        method: "PATCH"
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess(`Features section ${data.data.isActive ? "activated" : "deactivated"} successfully!`);
        setFeaturesData(data.data);
      } else {
        setError(data.message || "Failed to toggle features section status");
      }
    } catch (err) {
      setError("Network error. Please try again later.");
      console.error("Error toggling features section:", err);
    }
  };

  const handleReset = async () => {
    try {
      const response = await fetch("https://ecom-22-fe6311cae608.herokuapp.com/api/features-section/reset", {
        method: "POST"
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess("Features section reset to default successfully!");
        setFeaturesData(data.data);
        setShowResetModal(false);
      } else {
        setError(data.message || "Failed to reset features section");
      }
    } catch (err) {
      setError("Network error. Please try again later.");
      console.error("Error resetting features section:", err);
    }
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden"></span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!featuresData) {
    return (
      <Container className="py-5">
        <Alert variant="danger">Failed to load features section data</Alert>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <h2 className="mb-4">Manage Features Section</h2>
      
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Section Status</h5>
          <Button 
            variant={featuresData.isActive ? "success" : "secondary"}
            onClick={handleToggleActive}
          >
            {featuresData.isActive ? <FaToggleOn /> : <FaToggleOff />} 
            {featuresData.isActive ? " Active" : " Inactive"}
          </Button>
        </Card.Header>
        <Card.Body>
          <p>
            Last updated: {new Date(featuresData.updatedAt).toLocaleString()}
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Section Content</h5>
        </Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              name="title"
              value={featuresData.title}
              onChange={handleInputChange}
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Subtitle</Form.Label>
            <Form.Control
              type="text"
              name="subtitle"
              value={featuresData.subtitle}
              onChange={handleInputChange}
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              name="description"
              value={featuresData.description}
              onChange={handleInputChange}
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Image URL</Form.Label>
            <Form.Control
              type="text"
              name="imageUrl"
              value={featuresData.imageUrl}
              onChange={handleInputChange}
              placeholder="https://example.com/image.jpg"
            />
            {featuresData.imageUrl && (
              <div className="mt-2">
                <img 
                  src={featuresData.imageUrl} 
                  alt="Preview" 
                  style={{ maxWidth: "200px", maxHeight: "200px" }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://picsum.photos/seed/error/200/200.jpg";
                  }}
                />
              </div>
            )}
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Button Text</Form.Label>
            <Form.Control
              type="text"
              name="buttonText"
              value={featuresData.buttonText}
              onChange={handleInputChange}
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Button Link</Form.Label>
            <Form.Control
              type="text"
              name="buttonLink"
              value={featuresData.buttonLink}
              onChange={handleInputChange}
            />
          </Form.Group>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Features List</h5>
          <Button variant="primary" onClick={addFeature}>
            <FaPlus /> Add Feature
          </Button>
        </Card.Header>
        <Card.Body>
          {featuresData.features.map((feature, index) => (
            <div key={index} className="d-flex mb-2">
              <Form.Control
                type="text"
                value={feature}
                onChange={(e) => handleFeatureChange(index, e.target.value)}
              />
              <Button 
                variant="outline-danger" 
                className="ms-2"
                onClick={() => removeFeature(index)}
              >
                <FaTrash />
              </Button>
            </div>
          ))}
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-between">
        <Button variant="secondary" onClick={() => setShowResetModal(true)}>
          <FaUndo /> Reset to Default
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              <span className="visually-hidden"></span>
            </>
          ) : (
            <><FaSave /> Save Changes</>
          )}
        </Button>
      </div>

      <Modal show={showResetModal} onHide={() => setShowResetModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reset to Default</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to reset the features section to default values? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResetModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleReset}>
            Reset
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default FeaturesSectionAdmin;